import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Navigation from "./components/common-components/navigation/navigation.js";
import NewReleasepage from "./pages/new-release/new-release-page.js";
import {
  Page,
  PageContent,
} from "./components/common-components/page/page.jsx";
import PageHeader from "./components/common-components/page-header/page-header.jsx";
import { useEffect, useState } from "react";
import MyReleasesPage from "./pages/my-releases/my-releases-page.js";
import LoginPage from "./pages/login/login-page.js";
import axios from "axios";
import { API_URL, AUTH_URL } from "./utils/url.js";
import routes from "./routes/routes.js";
import AnalyticsPage from "./pages/analytics/analytics-page.js";
import AllReleasesPage from "./pages/all-releases/all-releases-page.js";
import ViewReleasePage from "./pages/view-release/view-release-page.js";
import {
  getLoggedInUser,
  isLoggedIn,
  removeAllCookies,
  setAuthCookies,
} from "./utils/auth.js";
import Cookies from "universal-cookie";
import { AuthContext } from "./context/auth.js";
import TermsOfReleasepage from "./pages/terms-of-release/terms-of-release-page.js";
import ProfilePage from "./pages/profile/profile-page.js";
import ConfigurePage from "./pages/configure/configure-page.js";
import "./globals.css";
import { Toaster } from "sonner";
import PricingPage from "./pages/pricing/pricing-page.jsx";

const cookies = new Cookies();

axios.defaults.baseURL = API_URL;

function App() {
  const [navigationExpanded, setNavigationExpanded] = useState(true);
  const [activeNav, setActiveNav] = useState(0);
  const [pageTitle, setPageTitle] = useState("");
  const [appInitialized, setAppInitialized] = useState(false);
  const [loginData, setLoginData] = useState(null);

  useEffect(() => {
    const mobileMedia = window.matchMedia("only screen and (max-width:960px)");
    const portraitMedia = window.matchMedia("(orientation: portrait)");

    const onMediaChange = () => {
      setNavigationExpanded(true);
    };

    mobileMedia.addEventListener("change", onMediaChange);
    portraitMedia.addEventListener("change", onMediaChange);

    let interceptor;

    if (isLoggedIn()) {
      if (cookies.get("accessToken")) {
        setLoginData({
          user: getLoggedInUser(),
          tokenResponse: {
            access_token: cookies.get("accessToken"),
            refresh_token: cookies.get("refreshToken"),
          },
        });

        // Fetch fresh user data
        axios
          .get(AUTH_URL + "/user/me")
          .then((response) => {
            const user = response.data.data.user;
            console.log("Fresh user data fetched:", user);
            setAuthCookies(
              {
                access_token: cookies.get("accessToken"),
                refresh_token: cookies.get("refreshToken"),
                expires_in: 3600, // Approximate, or we can skip updating tokens if not needed
              },
              user
            );
            setLoginData((prev) => ({ ...prev, user }));
          })
          .catch((err) => {
            console.error("Failed to fetch fresh user data", err);
          });
      }

      interceptor = axios.interceptors.request.use(
        async function (config) {
          try {
            if (cookies.get("accessToken") || config.url.includes("/oauth")) {
              return config;
            }
            const oauthApi = AUTH_URL + "/oauth/token";
            const params = new URLSearchParams();
            params.append("grant_type", "refresh_token");
            params.append("client_id", "application_client");
            params.append("refresh_token", cookies.get("refreshToken"));
            const payload = {
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
            };
            let tokenResponse = await axios.post(oauthApi, params, payload);
            tokenResponse = tokenResponse.data;
            setAuthCookies(tokenResponse);
            let userResponse = await axios.get(AUTH_URL + "/user/me", {
              headers: {
                Authorization: `Bearer ${tokenResponse.access_token}`,
              },
            });
            const user = userResponse.data.data.user;
            console.log("User fetched from Auth:", user);
            setAuthCookies(tokenResponse, user);
            setLoginData({ user, tokenResponse });
            axios.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${tokenResponse.access_token}`;
            config.headers.Authorization = `Bearer ${tokenResponse.access_token}`;
            return config;
          } catch (error) {
            if (
              error &&
              error.response &&
              (error.response.status === 400 || error.response.status === 401)
            ) {
              removeAllCookies();
              window.location = "/";
            } else {
              console.error(error);
              return Promise.reject(error);
            }
          }
        },
        (error) => {
          Promise.reject(error);
        }
      );
    }
    setAppInitialized(true);

    return () => {
      setAppInitialized(false);
      mobileMedia.removeEventListener("change", onMediaChange);
      portraitMedia.removeEventListener("change", onMediaChange);
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  if (!appInitialized) return null;

  return (
    <div className="App">
      <AuthContext.Provider value={{ loginData, setLoginData }}>
        <Navigation
          navigationExpanded={navigationExpanded}
          setNavigationExpanded={setNavigationExpanded}
          activeNav={activeNav}
          setActiveNav={setActiveNav}
          setPageTitle={setPageTitle}
        ></Navigation>
        <Routes>
          <Route
            path="/"
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <LoginPage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.newRelease}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <NewReleasepage />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.editRelease + "/:albumId"}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <NewReleasepage />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.myReleases}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <MyReleasesPage />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.allReleases}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <AllReleasesPage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.viewRelease + "/:albumId"}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <ViewReleasePage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.analytics}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <AnalyticsPage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.me}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <ProfilePage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.configure}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <ConfigurePage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.user + "/:id"}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <ProfilePage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path={routes.termsOfRelease}
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <TermsOfReleasepage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
          <Route
            path="/pricing"
            element={
              <Page>
                <PageHeader
                  setNavigationExpanded={setNavigationExpanded}
                  pageTitle={pageTitle}
                ></PageHeader>
                <PageContent>
                  <PricingPage setPageTitle={setPageTitle} />
                </PageContent>
              </Page>
            }
          />
        </Routes>
        <Toaster position="top-right" />
      </AuthContext.Provider>
    </div>
  );
}

function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWithRouter;
